// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import './App.css';
import logo from './assets/logo.png';

// Import pages
import Home from './pages/Home';
import About from './pages/About';
import Racing from './pages/Racing';
import Activities from './pages/Activities';
import TrainingLog from './pages/TrainingLog';
import MeetInfo from './pages/MeetInfo';
import RacingArchive from './pages/RacingArchive';

const navLinks = [
  { to: '/', label: 'Home', emoji: '🏠', end: true },
  { to: '/about', label: 'About', emoji: '🦅' },
  { to: '/activities', label: 'Activities', emoji: '🏃' },
  { to: '/racing', label: 'Racing', emoji: '🏁' },
  { to: '/training-log', label: 'Training Log', emoji: '📊' }
];

// Every page should open at the top, not wherever the previous page was scrolled.
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the mobile menu whenever we land on a new page.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <header className="header">
      <div className="nav-container">
        <Link to="/" className="logo" aria-label="DC Hawks Track Club home">
          <img src={logo} className="logo-image" alt="" />
          {/* Not an <h1>: each page owns its own heading */}
          <div className="logo-text">
            <span className="logo-title">DC HAWKS</span>
            <span className="logo-subtitle">Track Club</span>
          </div>
        </Link>

        <button
          type="button"
          className="nav-toggle"
          aria-expanded={menuOpen}
          aria-controls="main-nav"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen(open => !open)}
        >
          <span className={`nav-toggle-bars${menuOpen ? ' open' : ''}`} />
        </button>

        <nav id="main-nav" className={`nav${menuOpen ? ' open' : ''}`}>
          {navLinks.map(({ to, label, emoji, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
            >
              <span className="nav-emoji" aria-hidden="true">{emoji}</span>
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}

function NotFound() {
  return (
    <div className="page">
      <div className="cta-section">
        <h2>Off the course! 🧭</h2>
        <p>We couldn't find that page—but there's plenty to explore.</p>
        <Link to="/" className="cta-button">Back to Home 🏠</Link>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-content">
        <nav className="footer-links" aria-label="Footer">
          <Link to="/activities">Activities</Link>
          <Link to="/racing">Race Calendar</Link>
          <Link to="/racing-archive">Past Seasons</Link>
          <Link to="/training-log">Training Log</Link>
          <a
            href="https://dareios82.github.io/hardy-xc-track-records/index.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Hardy MS Records ↗
          </a>
          <a href="mailto:dario.caldara@gmail.com">Contact Coach Dario</a>
        </nav>
        <p>&copy; {new Date().getFullYear()} DC Hawks Track Club. All rights reserved.</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <ScrollToTop />
        <Header />

        {/* Page Content */}
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/racing" element={<Racing />} />
            <Route path="/racing-archive" element={<RacingArchive />} />
            <Route path="/activities" element={<Activities />} />
            <Route path="/training-log" element={<TrainingLog />} />
            <Route path="/meet-info" element={<MeetInfo />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        <Footer />
      </div>
    </Router>
  );
}

export default App;
